/* Scope native search to an explicit known query phone or browsing context.
 * No product filtering, compatibility guesses, or global preference writes. */
(() => {
  const SIZE = 'filter.v.option.size';
  const OPT_OUT = 'gc_search_phone';
  const PHONE_KEY = 'gc_selected_phone_model';
  const BRAND_KEY = 'gc_selected_phone_brand';
  const decoratedLinks = new WeakMap();
  const predictiveLinks = new WeakMap();
  const predictivePresentation = new WeakMap();
  const presentedInputs = new WeakSet();
  const presentedPanels = new WeakSet();
  const route = new URL(document.currentScript?.dataset.searchUrl || window.Theme?.routes.search_url || '/search', location.origin);
  const isSearch = (url) => url.origin === location.origin && url.pathname.replace(/\/$/, '') === route.pathname.replace(/\/$/, '');
  const clean = (value) => typeof value === 'string' && value.length <= 120 && !/[\u0000-\u001f\u007f]/.test(value) ? value.trim() : '';

  // The existing phone selector persists canonical values together with a brand.
  // Validate that contract; never manufacture or infer an unknown model value.
  function canonical(model, brand) {
    const value = clean(model);
    if (value !== model || !value) return '';
    if (brand === 'iphone' && value.startsWith('iPhone ') && value.length > 7) return value;
    if (brand === 'samsung' && value.startsWith('Samsung Galaxy ') && value.length > 15) return value;
    return '';
  }

  function savedPhone() {
    try { return canonical(localStorage.getItem(PHONE_KEY), localStorage.getItem(BRAND_KEY)); }
    catch { return ''; }
  }


  const QUERY_MODE = 'gc_search_match';
  const RAW_QUERY = 'gc_query';
  const STYLE_WORDS = new Set(['floral', 'cute', 'minimal', 'coquette', 'celestial', 'abstract']);
  // Read the already rendered, verified selector list; never fetch a catalog or
  // manufacture a variant/model from search text. Longest full aliases win.
  function phoneIntent(raw) {
    if (typeof raw !== 'string' || raw.length > 160 || /[:"()]/.test(raw)) return null;
    let cfg;
    try { cfg = JSON.parse(document.querySelector('[data-gc-saved-models]')?.textContent || '{}'); } catch { return null; }
    if (cfg.complete !== true || !Array.isArray(cfg.models) || !Number.isSafeInteger(cfg.expected) ||
        cfg.expected < 1 || cfg.models.length !== cfg.expected || new Set(cfg.models).size !== cfg.expected ||
        !cfg.models.every(value => typeof value === 'string' && /^(iPhone |Samsung Galaxy )/.test(value))) return null;
    // A model modifier cannot become a design keyword when the full model is
    // absent. Consume entire modifier chains, including compact ProAir/UltraPlus.
    // Complete listed models still match before this trailing guard.
    const matches = [];
    for (const model of cfg.models) {
      const aliases = model.startsWith('Samsung Galaxy ') ? [model, model.slice(8), model.slice(15)] : /^iPhone \d+e$/.test(model) ? [model, model.slice(7)] : [model];
      for (const alias of aliases) {
        const tokens = alias.match(/[a-z]+|[0-9]+/gi);
        if (!tokens || tokens.join('').toLowerCase() !== alias.replace(/\s/g, '').toLowerCase()) continue;
        const re = new RegExp('(^|[^\\p{L}\\p{N}])(' + tokens.join('\\s*') + ')(?![\\p{L}\\p{N}]|\\s*(?:(?:pro|max|plus|ultra|fe|mini|air|edge|lite|se|5g|4g)\\s*)+\\b)', 'giu');
        for (const match of raw.matchAll(re)) {
          const start = match.index + match[1].length;
          if (/^\d+e$/.test(alias) && /(?:samsung|galaxy)\s*$/i.test(raw.slice(0,start))) continue;
          matches.push({model, start, end:match.index + match[0].length});
        }
      }
    }
    matches.sort((a,b)=>(b.end-b.start)-(a.end-a.start));
    const spans=[];
    for (const match of matches) if (!spans.some(span=>match.start<span.end && match.end>span.start)) spans.push(match);
    const phoneLike = /\b(?:(?:iphone|samsung(?:\s*galaxy)?|galaxy)\s*(?:[a-z]*\d[a-z0-9]*|x[rs]?[a-z0-9]*|se[a-z0-9]*)|s\s*\d{2,}[a-z0-9]*|\d+\s*e[a-z0-9]*)\b/i;
    const unbrandedModel = /\b\d+\s*(?:pro|max|plus|ultra|fe|mini|air|edge|lite|se)[a-z0-9]*\b/i;
    const numberedModifier = /\b(?:(?:pro|max|plus|ultra|fe|mini|air|edge|lite|se)\s*)+\d+[a-z0-9]*\b/i;
    if (!spans.length) return phoneLike.test(raw) || unbrandedModel.test(raw) ? {ambiguous:true} : null;
    if (spans.length !== 1) return {ambiguous:true};
    const span=spans[0], remaining=(raw.slice(0,span.start)+' '+raw.slice(span.end)).trim().replace(/\s+/g,' ');
    if (phoneLike.test(remaining) || unbrandedModel.test(remaining) || numberedModifier.test(remaining) || (remaining && !keywordExpression(remaining))) return {ambiguous:true};
    return {model:span.model, remaining, expression:remaining ? keywordExpression(remaining) : '*'};
  }

  function keywordExpression(raw) {
    if (typeof raw !== 'string' || raw.length > 160) return null;
    const words = raw.trim().split(/\s+/u);
    if (!raw.trim() || words.length > 12 || words.some(word => !/^[\p{L}\p{N}]+(?:[-’'][\p{L}\p{N}]+)*$/u.test(word) || /^(AND|OR|NOT)$/i.test(word))) return null;
    return words.map(word => {
      const fields = ['title', 'body', 'tag', 'product_type', 'variants.title'];
      const parts = fields.map(field => field + ':"' + word + '"');
      if (STYLE_WORDS.has(word.toLowerCase())) parts.push('tag:"style-' + word.toLowerCase() + '"');
      return '(' + parts.join(' OR ') + ')';
    }).join(' AND ');
  }
  function keywordState(url) {
    const queries = url.searchParams.getAll('q');
    if (queries.length !== 1) return { raw: '', mode: 'advanced', valid: false };
    const q = queries[0];
    const raws = url.searchParams.getAll(RAW_QUERY);
    const modes = url.searchParams.getAll(QUERY_MODE);
    if (raws.length === 1 && modes.length === 1 && modes[0] === 'phone') {
      const intent=phoneIntent(raws[0]);
      if (intent?.model && intent.expression === q) return {raw:raws[0], mode:'phone', valid:true};
    }
    if (raws.length === 1 && modes.length === 1 && modes[0] === 'keywords' && keywordExpression(raws[0]) === q)
      return { raw: raws[0], mode: 'keywords', valid: true };
    if (modes.length === 1 && modes[0] === 'broad' && (raws.length === 0 || (raws.length === 1 && raws[0] === q)))
      return { raw: q, mode: 'broad', valid: true };
    return { raw: q, mode: keywordExpression(q) ? 'plain' : 'advanced', valid: false };
  }
  function applyKeywordScope(target, current) {
    if (target.searchParams.getAll('q').length !== 1 || !target.searchParams.get('q').trim()) {
      target.searchParams.delete(RAW_QUERY); target.searchParams.delete(QUERY_MODE); return;
    }
    const state = keywordState(target);
    if (state.valid) return;
    const prior = keywordState(current);
    // Facet/clear links can omit our parameters while retaining the encoded q.
    if (prior.valid && (target.searchParams.get('q') === current.searchParams.get('q') || target.searchParams.get('q') === prior.raw)) {
      target.searchParams.set(QUERY_MODE, prior.mode);
      target.searchParams.set(RAW_QUERY, prior.raw);
      target.searchParams.set('q', prior.mode === 'phone' ? phoneIntent(prior.raw).expression : prior.mode === 'keywords' ? keywordExpression(prior.raw) : prior.raw);
      return;
    }
    target.searchParams.delete(RAW_QUERY); target.searchParams.delete(QUERY_MODE);
    const expression = keywordExpression(state.raw);
    if (!expression) return; // Explicit field/operator syntax stays native.
    target.searchParams.set('q', expression);
    target.searchParams.set(RAW_QUERY, state.raw);
    target.searchParams.set(QUERY_MODE, 'keywords');
  }
  function displayQueryTitle(title, encoded, raw) {
    // Shopify may HTML-escape query quotes inside document.title. Decode only
    // the display string, never the request query or authorization markers.
    const decoded = String(title).replace(/&quot;|&#0*34;|&#x0*22;/gi, '"');
    return decoded.includes(encoded) ? decoded.split(encoded).join(raw) : title;
  }
  function presentKeywordSearch() {
    if (!isSearch(new URL(location.href))) return;
    const url = new URL(location.href), state = keywordState(url);
    const encoded = url.searchParams.get('q');
    if (!state.valid || !encoded) return;
    for (const input of document.querySelectorAll('.search-page-input[name="q"]')) {
      if (input.value === encoded || (!input.value && !presentedInputs.has(input))) input.value = state.raw;
      presentedInputs.add(input);
    }
    for (const root of document.querySelectorAll('[data-gc-query-display]')) {
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      let node;
      while ((node = walker.nextNode())) if (!['SCRIPT', 'STYLE'].includes(node.parentElement?.tagName) && node.nodeValue.includes(encoded)) node.nodeValue = node.nodeValue.split(encoded).join(state.raw);
    }
    const nextTitle = displayQueryTitle(document.title, encoded, state.raw);
    if (document.title !== nextTitle) document.title = nextTitle;
    for (const panel of document.querySelectorAll('[data-gc-keyword-mode]')) {
      presentedPanels.add(panel);
      if (state.mode === 'phone') { panel.hidden = true; continue; }
      panel.hidden = false;
      panel.querySelector('[data-gc-keyword-label]').textContent = state.mode === 'keywords' ? 'Keyword search' : 'Broader search · Includes related results';
      const link = panel.querySelector('a');
      const next = new URL(url.href);
      next.searchParams.delete('page');
      next.searchParams.set('q', state.mode === 'keywords' ? state.raw : keywordExpression(state.raw) || state.raw);
      next.searchParams.set(RAW_QUERY, state.raw);
      next.searchParams.set(QUERY_MODE, state.mode === 'keywords' ? 'broad' : 'keywords');
      link.href = next.href;
      link.textContent = state.mode === 'keywords' ? 'Try broader search' : 'Use keyword search';
    }
  }

  function scopeURL(input, options = {}) {
    const target = new URL(input, location.href);
    if (!isSearch(target)) return target;
    const currentURL = new URL(options.current || location.href);
    const rawQueries=target.searchParams.getAll('q'), state=keywordState(target);
    // Only a fresh ordinary query supplies intent. Explicit URL facets/model or
    // all-phone opt-out win; normalized/history links are never reinterpreted.
    const explicit=target.searchParams.has(SIZE)||target.searchParams.has('model')||target.searchParams.get(OPT_OUT)==='all';
    if (!explicit && !options.removeSize && !state.valid && rawQueries.length===1 &&
        !(keywordState(currentURL).valid && rawQueries[0]===currentURL.searchParams.get('q'))) {
      const intent=phoneIntent(rawQueries[0]);
      if (intent?.model) {
        target.searchParams.set('q',intent.expression);
        target.searchParams.set(RAW_QUERY,rawQueries[0]);target.searchParams.set(QUERY_MODE,'phone');
        target.searchParams.set('model',intent.model);target.searchParams.set(SIZE,intent.model);
        target.searchParams.delete('page');
      } else if (intent?.ambiguous) target.searchParams.set(OPT_OUT,'all');
    }
    applyKeywordScope(target, currentURL);
    // Empty/reset search uses a recommendation branch, not native matched
    // results. Do not attach a facet and imply that branch was filtered.
    const queries = target.searchParams.getAll('q');
    if (queries.length !== 1 || !queries[0].trim()) return target;
    const current = new URL(options.current || location.href);
    // Presence is intentional, including empty or multiple Size values. Never
    // narrow multi-Size or replace malformed explicit URLs with a saved phone.
    if (target.searchParams.has(SIZE)) return target;
    if (options.removeSize && current.searchParams.has(SIZE)) {
      target.searchParams.delete('model');
      target.searchParams.set(OPT_OUT, 'all');
      target.searchParams.delete('page');
      return target;
    }
    if (target.searchParams.has('model')) {
      const models = target.searchParams.getAll('model');
      const model = models.length === 1 ? clean(models[0]) : '';
      if (model) {
        target.searchParams.set(SIZE, model);
        target.searchParams.delete(OPT_OUT);
      }
      return target;
    }
    if (target.searchParams.get(OPT_OUT) === 'all') return target;
    if (current.searchParams.has(SIZE)) {
      for (const value of current.searchParams.getAll(SIZE)) target.searchParams.append(SIZE, value);
      if (current.searchParams.getAll('model').length === 1) target.searchParams.set('model', current.searchParams.get('model'));
      return target;
    }
    if (current.searchParams.has('model')) {
      const models = current.searchParams.getAll('model');
      const model = models.length === 1 ? clean(models[0]) : '';
      if (model) { target.searchParams.set('model', model); target.searchParams.set(SIZE, model); }
      return target;
    }
    if (isSearch(current) && current.searchParams.get(OPT_OUT) === 'all') {
      target.searchParams.set(OPT_OUT, 'all');
      return target;
    }
    const phone = savedPhone();
    if (phone) { target.searchParams.set('model', phone); target.searchParams.set(SIZE, phone); }
    return target;
  }

  function ensureScope() {
    if (!isSearch(new URL(location.href))) return;
    const target = scopeURL(location.href);
    if (target.href !== location.href) {
      const results = document.querySelector('.gc-search-results');
      if (results) { results.inert = true; results.setAttribute('aria-busy', 'true'); }
      location.replace(target.href);
    }
  }

  function predictiveTarget(input, query) {
    const original = new URL(input, location.href);
    if (!String(query || '').trim() || original.origin !== location.origin ||
      !/\/products\/[^/]+\/?$/.test(original.pathname) || original.searchParams.has('variant') || original.searchParams.has('model')) return original;
    const target = new URL(route.href);
    target.searchParams.set('q', query);
    const scoped = scopeURL(target.href);
    return scoped.searchParams.getAll(SIZE).some((value) => clean(value)) ? scoped : original;
  }

  function scopePredictiveLink(link, query) {
    const previous = predictiveLinks.get(link);
    const original = previous?.decorated === link.href ? previous.original : link.href;
    const target = predictiveTarget(original, query);
    link.href = target.href;
    if (target.href !== original) predictiveLinks.set(link, { original, decorated: target.href });
    else predictiveLinks.delete(link);
    return target.href !== original ? target : null;
  }

  // Predictive family summaries are catalog-wide, not model-qualified. Until
  // the native filtered results establish compatibility, show only the design
  // preview and a check-options action, never a family price or style claim.
  function restorePredictivePresentation(card) {
    const state = predictivePresentation.get(card);
    if (!state) return;
    for (const [element, attributes] of state) {
      for (const [name, value] of attributes) {
        if (value === null) element.removeAttribute(name);
        else element.setAttribute(name, value);
      }
    }
    predictivePresentation.delete(card);
  }

  function scopePredictivePresentation(card, label) {
    restorePredictivePresentation(card);
    const state = [];
    const remember = (element, names) => state.push([element, names.map((name) => [name, element.getAttribute(name)])]);
    // Hide the entire price container, including sale/unit-price descriptions.
    for (const element of card.querySelectorAll('.resource-card__tag, [ref="priceContainer"], .resource-card__content > .price')) {
      remember(element, ['style', 'aria-hidden']);
      element.style.setProperty('display', 'none', 'important');
      element.setAttribute('aria-hidden', 'true');
    }
    const title = card.querySelector('.resource-card__title')?.textContent.trim() || 'Design';
    for (const link of card.querySelectorAll('a[href]')) {
      remember(link, ['aria-label']);
      link.setAttribute('aria-label', title + '. ' + label);
      for (const summary of link.querySelectorAll('.visually-hidden')) {
        remember(summary, ['aria-hidden']);
        summary.setAttribute('aria-hidden', 'true');
      }
    }
    for (const image of card.querySelectorAll('.resource-card__image')) {
      remember(image, ['alt']);
      image.setAttribute('alt', title + ' design preview; device and case style may differ.');
    }
    predictivePresentation.set(card, state);
  }

  function preparePredictive(component) {
    const query = component.querySelector('input[name="q"]')?.value || '';
    for (const card of component.querySelectorAll('.predictive-search-results__card--product')) {
      let scoped;
      for (const link of card.querySelectorAll('a[href]')) scoped = scopePredictiveLink(link, query) || scoped;
      let note = card.querySelector('[data-gc-search-phone-note]');
      if (!scoped) { scopePredictivePresentation(card, 'Design preview. Check phone and case options before choosing.'); note?.remove(); continue; }
      const label = 'Check compatible options for ' + scoped.searchParams.getAll(SIZE).filter(Boolean).join(', ');
      scopePredictivePresentation(card, label);
      if (!note) {
        note = document.createElement('p');
        note.setAttribute('data-gc-search-phone-note', '');
        note.className = 'resource-card__subtext paragraph';
        (card.querySelector('.resource-card__content') || card).appendChild(note);
      }
      note.textContent = 'Design preview · ' + label;
    }
  }

  function searchForm(form) {
    return form instanceof HTMLFormElement && form.getAttribute('role') === 'search' &&
      String(form.method || 'get').toLowerCase() === 'get' && isSearch(new URL(form.action, location.href));
  }

  function scopedFormData(form, data) {
    // Hidden inputs from a prior submitted/restored form are our state, not an
    // authored Size override. Native form.submit() must also refresh them.
    form.querySelectorAll('[data-gc-search-phone-input]').forEach((input) => {
      const authored = Array.from(form.elements).some((element) => element.name === input.name &&
        !element.disabled && !element.hasAttribute('data-gc-search-phone-input'));
      if (!authored) data.delete(input.name);
    });
    const target = new URL(form.action, location.href);
    target.search = new URLSearchParams(data).toString();
    const scoped = scopeURL(target.href);
    if (scoped.searchParams.has('q')) data.set('q', scoped.searchParams.get('q'));
    for (const name of [RAW_QUERY, QUERY_MODE]) {
      data.delete(name);
      for (const value of scoped.searchParams.getAll(name)) data.append(name, value);
    }
    for (const name of [SIZE, 'model', OPT_OUT, RAW_QUERY, QUERY_MODE]) {
      if (data.has(name)) continue;
      for (const value of scoped.searchParams.getAll(name)) data.append(name, value);
    }
    return data;
  }

  // Capture runs before component bubbling handlers and normal form navigation.
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!searchForm(form)) return;
    form.querySelectorAll('[data-gc-search-phone-input]').forEach((input) => input.remove());
    const original = new FormData(form);
    const scoped = scopedFormData(form, original);
    for (const name of [SIZE, 'model', OPT_OUT, RAW_QUERY, QUERY_MODE]) {
      if (Array.from(form.elements).some((element) => element.name === name && !element.disabled)) continue;
      for (const value of scoped.getAll(name)) {
        const input = document.createElement('input');
        input.type = 'hidden'; input.name = name; input.value = value;
        input.setAttribute('data-gc-search-phone-input', '');
        form.appendChild(input);
      }
    }
  }, true);

  // Also covers direct native form.submit(), which emits no submit event.
  document.addEventListener('formdata', (event) => {
    if (searchForm(event.target)) scopedFormData(event.target, event.formData);
  }, true);

  for (const name of ['click', 'auxclick']) document.addEventListener(name, (event) => {
    if (name === 'auxclick' && event.button !== 1) return;
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!link) return;
    const predictive = link.closest('predictive-search-component');
    if (predictive && link.closest('.predictive-search-results__card--product')) {
      scopePredictiveLink(link, predictive.querySelector('input[name="q"]')?.value || '');
      return;
    }
    const previous = decoratedLinks.get(link);
    const originalHref = previous?.decorated === link.href ? previous.original : link.href;
    const original = new URL(originalHref, location.href);
    if (!isSearch(original)) return;
    const scoped = scopeURL(original.href).href;
    link.href = scoped;
    if (scoped === originalHref) decoratedLinks.delete(link);
    else decoratedLinks.set(link, { original: originalHref, decorated: scoped });
  }, true);

  document.addEventListener('gc:phone-model-change', (event) => {
    const model = canonical(event.detail?.model, event.detail?.brand);
    if (!model) return;
    document.querySelectorAll('predictive-search-component').forEach(preparePredictive);
    if (!isSearch(new URL(location.href))) return;
    const target = new URL(location.href);
    if (!target.searchParams.get('q')?.trim()) return;
    target.searchParams.set('model', model);
    target.searchParams.set(SIZE, model);
    target.searchParams.delete(OPT_OUT);
    target.searchParams.delete('page');
    if (target.href !== location.href) location.assign(target.href);
  });

  document.addEventListener('gc:saved-phone-change', (event) => {
    const model=canonical(event.detail?.model,event.detail?.brand);
    document.querySelectorAll('predictive-search-component').forEach(preparePredictive);
    if(!isSearch(new URL(location.href)))return;
    const target=new URL(location.href);
    ['model',SIZE,'device','page'].forEach(key=>target.searchParams.delete(key));
    if(model){target.searchParams.set('model',model);target.searchParams.set(SIZE,model);target.searchParams.delete(OPT_OUT);}
    else target.searchParams.set(OPT_OUT,'all');
    if(target.href!==location.href)location.assign(target.href);
  });

  window.GoCraftlySearchPhoneContext = Object.freeze({ phoneIntent, keywordExpression, keywordState, applyKeywordScope, presentKeywordSearch, scopeURL, savedPhone, canonical, predictiveTarget, preparePredictive });
  window.addEventListener('pageshow', () => { ensureScope(); presentKeywordSearch(); });
  document.addEventListener('DOMContentLoaded', presentKeywordSearch);
  document.addEventListener('shopify:section:load', presentKeywordSearch);
  if (isSearch(new URL(location.href))) {
    const searchRoot = document.querySelector('#MainContent');
    if (searchRoot) new MutationObserver((records) => {
      const newPanel = records.some(record => Array.from(record.addedNodes).some(node => {
        if (!(node instanceof Element)) return false;
        const panels = node.matches('[data-gc-keyword-mode]') ? [node] : node.querySelectorAll('[data-gc-keyword-mode]');
        return Array.from(panels).some(panel => !presentedPanels.has(panel));
      }));
      if (newPanel) presentKeywordSearch();
    }).observe(searchRoot, { childList: true, subtree: true });
  }
  ensureScope();
  presentKeywordSearch();
})();

